import React, { createContext, useContext, useState, useCallback } from "react";
import { ToastContainer, ToastNotification } from "@/components/Toast";

interface ToastContextValue {
  showToast: (
    title: string,
    message: string,
    options?: {
      duration?: number;
      onClick?: () => void;
    },
  ) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export const useToastNotifications = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToastNotifications must be used within ToastProvider");
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [notifications, setNotifications] = useState<ToastNotification[]>([]);

  const showToast = useCallback(
    (
      title: string,
      message: string,
      options?: {
        duration?: number;
        onClick?: () => void;
      },
    ) => {
      const id = `toast-${Date.now()}-${Math.random()}`;
      const notification: ToastNotification = {
        id,
        title,
        message,
        duration: options?.duration || 5000,
        onClick: options?.onClick,
      };

      setNotifications((prev) => [...prev, notification]);
    },
    [],
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer
        notifications={notifications}
        onRemove={removeNotification}
      />
    </ToastContext.Provider>
  );
};
